import {
  LottieView,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import envData from 'env.json';
import ReservationsModule from 'components/Reservations/ReservationsModule';
import {LOADER_ANIMATION} from 'assets/LottieAnimations';
import {usePropertyList} from 'hooks/usePropertyList';
import {
  ReservationDate,
  ReservationState,
  ReservationStatus,
} from 'models/Reservations';
import {useSelector} from 'Provider';
import React, {useCallback, useMemo} from 'react';
import {sortedMarketsSelector} from 'store/markets/markets.selectors';
import {
  propertiesErrorSelector,
  propertiesLoadingSelector,
} from 'store/properties/properties.selectors';
import {
  reservationsFailedSelector,
  reservationsLoadingSelector,
  reservationsSelector,
} from 'store/reservations/reservations.selectors';
import ReservationCard from 'components/Reservations/ReservationCard';
import OffersErrorDisplay from 'components/Offers/ErrorDisplay';
import PlanYourTripModule from 'components/PlanYourTripModule';

const {API_URL} = envData;

export interface ConnectedReservationsModuleProps {
  activeTab?: ReservationState | ReservationStatus;
  isHomeScreen: boolean;
}

export const generateCheckInUrl = (
  reservationID: string,
  propertyCode: string,
  confCode: string,
  trackingParam: string
): string => {
  return `${API_URL}/checkin/?reservationId=${reservationID}&propCode=${propertyCode}&confCode=${confCode}&rd=${trackingParam}`;
};

const ConnectedReservationsModule: React.FC<
  ConnectedReservationsModuleProps
> = props => {
  const {activeTab, isHomeScreen} = props;
  const isDesktop = useIsDesktop();
  const allReservations = useSelector(reservationsSelector);

  const reservations = useMemo(
    () =>
      allReservations.filter(reservation => {
        const reservationDate = new Date(
          reservation.checkInDate?.replace(/-/g, '/')
        );
        const currentDate = new Date();
        return (
          currentDate.getTime() - reservationDate.getTime() <=
          1000 * 60 * 60 * 24 * 90
        );
      }),
    [allReservations]
  );

  const propertyCodes = useMemo(
    () => [
      ...new Set(reservations.map(reservation => reservation.propertyCode)),
    ],
    [reservations]
  );

  const namedPropertyList = usePropertyList(propertyCodes).offerProperties;
  const markets = useSelector(sortedMarketsSelector);
  const isReservationsLoading = useSelector(reservationsLoadingSelector);
  const isPropertiesLoading = useSelector(propertiesLoadingSelector);
  const isLoading = isReservationsLoading || isPropertiesLoading;
  const reservationFailed = useSelector(reservationsFailedSelector);
  const propertiesFailed = useSelector(propertiesErrorSelector);
  const hasFailed = reservationFailed || propertiesFailed;

  const getReservationCard = useCallback(
    (reservation: ReservationDate) => {
      const property = namedPropertyList.find(
        namedProperty =>
          namedProperty.id === reservation.propertyCode.toLowerCase()
      );

      const market = markets.find(
        m => m.id === property?.marketCode?.toLowerCase()
      );

      if (!property || !market) {
        return null;
      }

      let imageUrl = property.thumbnail?.url ?? '';

      if (imageUrl) {
        imageUrl = API_URL + imageUrl;
      }

      let checkInUrl = '';

      const detailsUrl = `${API_URL}/book/?view=findreservation&confCode=${
        reservation.confirmationCode
      }&lastName=${encodeURIComponent(
        reservation.lastName
      )}&arrivalDate=${encodeURIComponent(
        reservation.checkInDate?.replaceAll('-', '/')
      )}&propcode=${reservation.propertyCode}`;

      const showDetails =
        reservation.status.toUpperCase() !== ReservationStatus.CANCELLED;

      let showCheckIn =
        property.digitalCheckInIsActive &&
        reservation.status.toUpperCase() !== ReservationStatus.CANCELLED &&
        reservation.state === ReservationState.FUTURE;

      let isCheckInDisabled = true;

      let checkInText = '';

      if (showCheckIn) {
        const checkInWindowStart = parseInt(
          property.digitalCheckInWindowStart ?? '0',
          10
        );
        const checkInWindowEnd = parseInt(
          property.digitalCheckInWindowEnd ?? '0',
          10
        );
        const checkInStart = new Date(
          (reservation.checkInDate + ' ' + property.checkIn)?.replace(/-/g, '/')
        );
        checkInStart.setHours(checkInStart.getHours() - checkInWindowStart);

        const checkInEnd = new Date(
          (reservation.checkInDate + ' ' + property.checkIn)?.replace(/-/g, '/')
        );
        checkInEnd.setHours(checkInEnd.getHours() + checkInWindowEnd);
        const currentTime = new Date();

        const isTooEarly = currentTime < checkInStart;
        const isTooLate = currentTime > checkInEnd;
        if (isTooLate) {
          showCheckIn = false;
        } else if (isTooEarly) {
          const timeTillCheckIn =
            checkInStart.getTime() - currentTime.getTime();
          const daysTillCheckIn = Math.ceil(
            timeTillCheckIn / (1000 * 60 * 60 * 24)
          );
          if (daysTillCheckIn > 1) {
            checkInText = daysTillCheckIn + ' Days Until Check-In';
          } else {
            const hoursTillCheckIn = Math.ceil(
              timeTillCheckIn / (1000 * 60 * 60)
            );
            if (hoursTillCheckIn > 1) {
              checkInText = hoursTillCheckIn + ' Hours Until Check-In';
            } else {
              const minutesTillCheckIn = Math.ceil(
                timeTillCheckIn / (1000 * 60)
              );
              const timeUnit = minutesTillCheckIn > 1 ? 'Minutes' : 'Minute';
              checkInText = `${minutesTillCheckIn} ${timeUnit} Until Check-In`;
            }
          }
        } else {
          isCheckInDisabled = false;
          const checkInDate = new Date(
            reservation.checkInDate?.replace(/-/g, '/')
          )
            .toISOString()
            .split('T')[0];

          const trackingParam = isHomeScreen
            ? 'snp_rewards_home'
            : 'snp_rewards_stays';

          checkInUrl = generateCheckInUrl(
            reservation.reservationID,
            reservation.propertyCode,
            reservation.confirmationCode,
            trackingParam
          );

          checkInText = 'EXPRESS CHECK-IN';
        }
      }

      return (
        <ReservationCard
          key={reservation.confirmationCode}
          propertyName={property.name ?? ''}
          marketName={market.name ?? ''}
          regionCode={reservation.regionCode}
          checkInDate={reservation.checkInDate}
          checkOutDate={reservation.checkOutDate}
          confirmationCode={reservation.confirmationCode}
          onPress={() => {}}
          imageUrl={imageUrl}
          adults={reservation.adults.toString()}
          kids={reservation.children.toString()}
          checkInUrl={checkInUrl}
          detailsUrl={detailsUrl}
          showCheckIn={showCheckIn ?? false}
          isCheckInDisabled={isCheckInDisabled}
          checkInText={checkInText}
          showDetails={showDetails}
          testID={property.name ?? ''}
          isHomeScreen={isHomeScreen}
        />
      );
    },
    [namedPropertyList, markets]
  );

  const filteredReservations = useMemo(() => {
    if (!reservations) {
      return [];
    }
    return reservations
      .filter(reservation => {
        const currentDate = new Date();
        const checkInDate = new Date(
          reservation.checkInDate?.replace(/-/g, '/')
        );
        const checkOutDate = new Date(
          reservation.checkOutDate?.replace(/-/g, '/')
        );

        // Determine if reservation is current (between check-in and check-out dates)
        const isCurrent =
          currentDate >= checkInDate && currentDate < checkOutDate;

        if (isHomeScreen) {
          return (
            (isCurrent || reservation.state === ReservationState.FUTURE) &&
            reservation.status.toUpperCase() !== ReservationStatus.CANCELLED
          );
        } else {
          if (activeTab === ReservationStatus.CANCELLED) {
            return reservation.status.toUpperCase() === activeTab;
          } else if (activeTab === ReservationState.FUTURE) {
            return (
              (isCurrent || reservation.state === ReservationState.FUTURE) &&
              reservation.status.toUpperCase() !== ReservationStatus.CANCELLED
            );
          } else if (activeTab === ReservationState.PAST) {
            return (
              !isCurrent &&
              reservation.status.toUpperCase() !==
                ReservationStatus.CANCELLED &&
              reservation.state === activeTab
            );
          } else {
            return (
              reservation.status.toUpperCase() !==
                ReservationStatus.CANCELLED && reservation.state === activeTab
            );
          }
        }
      })
      .sort((a, b) => {
        const firstReservationDate = new Date(
          a.checkInDate?.replace(/-/g, '/')
        );
        const secondReservationDate = new Date(
          b.checkInDate?.replace(/-/g, '/')
        );
        if (activeTab === ReservationState.FUTURE || isHomeScreen) {
          return (
            firstReservationDate.getTime() - secondReservationDate.getTime()
          );
        } else {
          return (
            secondReservationDate.getTime() - firstReservationDate.getTime()
          );
        }
      })
      .slice(0, isHomeScreen ? 1 : undefined);
  }, [reservations, activeTab]);

  return (
    <>
      {isLoading ? (
        isHomeScreen ? null : (
          <LottieView
            source={LOADER_ANIMATION}
            loop
            autoPlay
            style={{
              width: isDesktop ? 550 : '100%',
              height: isDesktop ? 550 : undefined,
            }}
          />
        )
      ) : hasFailed ? (
        <OffersErrorDisplay />
      ) : isHomeScreen && filteredReservations?.length === 0 ? (
        <PlanYourTripModule />
      ) : (
        <ReservationsModule
          isLoading={isLoading}
          hasFailed={hasFailed}
          reservations={filteredReservations}
          isHomeScreen={isHomeScreen}
          activeTab={activeTab}
          getReservationCard={getReservationCard}
        />
      )}
    </>
  );
};

export default ConnectedReservationsModule;
